import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Select, MenuItem, RadioGroup, FormControlLabel, Radio,
  InputLabel, FormControl, TablePagination, IconButton, Autocomplete,
  Grid, InputAdornment, Typography, Chip, Tooltip, CircularProgress,
  Box,
} from "@mui/material";
import { Delete, Edit, Search, Download, Refresh, CheckCircle, ErrorOutline } from "@mui/icons-material";
import {
  collection, addDoc, onSnapshot, deleteDoc, doc, updateDoc, serverTimestamp,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../FirebaseConfig";
import { createUserAccount, sendTritekConnectInvite, inviteRecipients } from "./UnifiedPay/api";
import { downloadCSV } from "./csv";

// Point this at your Node backend base URL (same place createUserAccount talks to)
const API_BASE = process.env.REACT_APP_API_BASE || "https://innovators.onrender.com";

// ─── Restricted UIDs (this group can only see orders belonging to each other) ──
const RESTRICTED_UIDS = new Set([
  "aBcsd3ZiJEOSIOGA0dDnLcxbXGu1",
  "Q9wvLEw80hRxrDhEUBpIeqdzKeh2",
  "bmGnB8vaS2el3u9OmtDtsDJU4ZC3",
  "wclOYVxJYERRLg2tpgzUu1sTD1k2",
  "bmO4oH84yiM8ZOxhkHT4sUMTTfy1",
  "wA7IxvOETcboWqPP4IxiIaG6XQA2",
]);

const getStaffName = (staff) => {
  if (!staff) return "";
  if (typeof staff === "string") return staff;
  if (typeof staff === "object" && staff.name) return String(staff.name);
  return String(staff);
};

// ─── Access check helper ───────────────────────────────────────────────────────
// Asks the Node backend (which proxies to the WP custom REST route) whether
// this order's user has the PMPro level + LearnPress course access they're
// owed. Looked up by wpUserId if we have it, otherwise falls back to email.
const checkAccess = async (order) => {
  try {
    const params = new URLSearchParams();
    if (order.wpUserId) params.set("wpUserId", order.wpUserId);
    else if (order.email) params.set("email", order.email);
    else return { checked: false };

    const res = await fetch(`${API_BASE}/check-access?${params.toString()}`);
    if (!res.ok) return { checked: false };

    const data = await res.json();
    if (!data.found) return { checked: true, found: false };

    return {
      checked: true,
      found: true,
      hasFullAccess: Boolean(data.has_full_access),
      hasAnyLevel: Boolean(data.has_any_level),
      noCoursesConfigured: Boolean(data.has_any_level) && (data.expected_courses || []).length === 0,
      missingCourses: data.missing_courses || [],
      expectedCourses: data.expected_courses || [],
    };
  } catch {
    return { checked: false };
  }
};

const ACCESS_CUTOFF = new Date("2026-06-01T00:00:00");

const OrdersTable = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [open, setOpen] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [newOrder, setNewOrder] = useState({
    name: "", email: "", phone: "", staff: "",
    paymentPlan: "part", paymentOption: "card", amountPaid: "", product: "", productId: ""
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({ paymentPlan: "", paymentOption: "", course: "" });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Access-status tracking, keyed by order.id
  const [accessStatus, setAccessStatus] = useState({}); // { [orderId]: { checked, found, hasFullAccess, ... } }
  const [accessChecking, setAccessChecking] = useState(false);
  const [fixingOrderId, setFixingOrderId] = useState(null);

  // ─── Bulk repair modal ─────────────────────────────────────────────────────
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkDone, setBulkDone] = useState(false);
  const [bulkLog, setBulkLog] = useState([]); // [{ id, name, email, course, state, detail }]
  const [bulkSummary, setBulkSummary] = useState({ total: 0, checked: 0, alreadyOk: 0, fixed: 0, failed: 0, skipped: 0 });
  const bulkCancelRef = useRef(false);

  // null when idle, { done, total, failed } while invites are sending
  const [inviteProgress, setInviteProgress] = useState(null);

  // Auth state — undefined means "not yet resolved", so we wait before rendering anything
  const [currentUser, setCurrentUser] = useState(undefined);
  const [isRestricted, setIsRestricted] = useState(false);

  const courseOptions = {
    "Data Analysis":      { price: 459, productId: 22767 },
    "Cybersecurity":      { price: 429, productId: 22766 },
    "Digital Marketing":  { price: 299, productId: 45867 },
    "Product Management": { price: 349, productId: 48478 },
    "PMBA":               { price: 489, productId: 51922 },
    "PMDA":               { price: 549, productId: 56409 },
    "BADA":               { price: 549, productId: 64347 },
    "Health Analyitcs":   { price: 429, productId: 69868 },
    BA: { price: 459, productId: 72791},
    PM: { price: 459, productId: 72790},
  };

  const courseList = Object.entries(courseOptions);

  // ─── Step 1: Wait for Firebase Auth to resolve before doing anything ────────
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setIsRestricted(Boolean(user && RESTRICTED_UIDS.has(user.uid)));
    });
    return () => unsubscribeAuth();
  }, []);

  // ─── Step 2: Only start Firestore listeners once auth is resolved ────────────
  useEffect(() => {
    if (currentUser === undefined) return;

    let unsubscribeOrders = () => {};

    const unsubscribeStaff = onSnapshot(collection(db, "influencers"), (snapshot) => {
      const allInfluencers = snapshot.docs.map((d) => d.data());
      const staff = allInfluencers.filter((u) => u.role === "user");
      setStaffList(staff.map((s) => s.name));

      const restrictedNames = new Set(
        allInfluencers
          .filter((u) => u.uid && RESTRICTED_UIDS.has(u.uid))
          .map((u) => u.name)
          .filter(Boolean)
      );

      unsubscribeOrders();

      unsubscribeOrders = onSnapshot(collection(db, "orders"), (orderSnapshot) => {
        let fetchedOrders = orderSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

        if (isRestricted) {
          fetchedOrders = fetchedOrders.filter((order) =>
            restrictedNames.has(getStaffName(order.staff))
          );
        }

        setOrders(fetchedOrders);
        setFilteredOrders(fetchedOrders);
      });
    });

    return () => {
      unsubscribeStaff();
      unsubscribeOrders();
    };
  }, [currentUser, isRestricted]);

  // ─── Client-side filtering ────────────────────────────────────────────────
  useEffect(() => {
    const from = dateFrom ? new Date(dateFrom).setHours(0, 0, 0, 0) : null;
    const to   = dateTo   ? new Date(dateTo).setHours(23, 59, 59, 999) : null;
    const q = (searchQuery || "").trim().toLowerCase();

    const filtered = orders.filter((order) => {
      const createdTime = order.createdAtISO ? new Date(order.createdAtISO).getTime() : null;
      const staffName = getStaffName(order.staff);
      const matchesSearch = !q ? true
        : [order.name, order.email, order.phone, staffName]
            .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
      const matchesPlan   = filters.paymentPlan   ? order.paymentPlan   === filters.paymentPlan   : true;
      const matchesOption = filters.paymentOption ? order.paymentOption === filters.paymentOption : true;
      const matchesCourse = filters.course        ? order.productName   === filters.course        : true;
      const matchesFrom   = from ? (createdTime ?? 0) >= from : true;
      const matchesTo     = to   ? (createdTime ?? 0) <= to   : true;
      return matchesSearch && matchesPlan && matchesOption && matchesCourse && matchesFrom && matchesTo;
    });

    setFilteredOrders(filtered);
    setPage(0);
  }, [orders, searchQuery, filters, dateFrom, dateTo]);

  // ─── Access status check for currently visible page of orders ───────────────
  // Only checks orders from 1 June 2026 onward, completed status, with an
  // email or wpUserId to look up — matches the scope of the access-repair effort.
  const runAccessCheck = useCallback(async (rows) => {
    setAccessChecking(true);
    const updates = {};

    await Promise.all(
      rows.map(async (order) => {
        const createdTime = order.createdAtISO ? new Date(order.createdAtISO) : null;
        const inScope = createdTime
          && createdTime >= ACCESS_CUTOFF
          && (order.email || order.wpUserId);

        if (!inScope) {
          updates[order.id] = { checked: false, outOfScope: true };
          return;
        }

        updates[order.id] = await checkAccess(order);
      })
    );

    setAccessStatus((prev) => ({ ...prev, ...updates }));
    setAccessChecking(false);
  }, []);

  // Re-run the access check whenever the visible page changes
  useEffect(() => {
    const visibleRows = filteredOrders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    if (visibleRows.length) runAccessCheck(visibleRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredOrders, page, rowsPerPage]);

  // Maps a historical/dead product ID to its current replacement.
  // PMBA orders created before the product swap still reference the old,
  // now-deleted product ID (22684) in Firestore — using it directly would
  // recreate the same "deleted product" failure this whole repair effort
  // was built to fix. We keep the Firestore record as-is (historical truth
  // of what was charged) and only substitute the live ID at fix-time.
  const LEGACY_PRODUCT_ID_MAP = {
    22684: 51922, // old PMBA product → current PMBA product
  };

  const resolveCurrentProductId = (productId) => {
    const numericId = Number(productId);
    return LEGACY_PRODUCT_ID_MAP[numericId] ?? productId;
  };

  // Core fix logic, reusable by both the single-row Fix Access button and
  // the bulk repair modal. Returns the createUserAccount result so callers
  // can report success/failure without duplicating the call.
  const fixOneOrder = async (order) => {
    const [firstName, ...rest] = (order.name || "").trim().split(" ");
    const lastName = rest.join(" ") || "";
    const liveProductId = resolveCurrentProductId(order.productId);

    return createUserAccount(
      { email: order.email, firstName, lastName, phone: order.phone },
      {
        amountPaid: Math.round(Number(order.amountPaid || 0) * 100),
        productId: liveProductId,
        source: order.paymentOption || "manual-fix",
        courseKey: order.productName || "",
        skipEmail: true,
      }
    );
  };

  // ─── Fix Access (single row) ──────────────────────────────────────────────
  // Re-runs createUserAccount with this order's stored data. Since the
  // deleted-product issue is now fixed, recreating the order via the normal
  // flow will correctly cascade into PMPro + LearnPress. The completed-order
  // email is suppressed for these backfill orders (skipEmail: true) since
  // the goal here is restoring access, not notifying the customer of a "new"
  // purchase — and the order confirmation email template has an unrelated
  // crash in one of its content blocks that we're sidestepping this way.
  const handleFixAccess = async (order) => {
    setFixingOrderId(order.id);
    try {
      const result = await fixOneOrder(order);

      if (result?.success) {
        // Re-check access after a short delay to let WP finish processing
        setTimeout(async () => {
          const status = await checkAccess(order);
          setAccessStatus((prev) => ({ ...prev, [order.id]: status }));
        }, 2000);
      } else {
        alert(`Fix attempt failed: ${result?.error || "unknown error"}`);
      }
    } catch (error) {
      console.error("Error fixing access:", error);
      alert("Something went wrong while trying to fix access. Check the console for details.");
    } finally {
      setFixingOrderId(null);
    }
  };

  // ─── Bulk repair (all in-scope orders) ────────────────────────────────────
  // Scans every order from the cutoff date onward with an email/wpUserId,
  // checks access, and fixes anything missing — one order at a time, with
  // live progress shown in the modal. Runs sequentially (not in parallel) to
  // avoid hammering WordPress with simultaneous order-creation requests.
  const runBulkRepair = async () => {
    bulkCancelRef.current = false;
    setBulkRunning(true);
    setBulkDone(false);
    setBulkLog([]);
    setBulkSummary({ total: 0, checked: 0, alreadyOk: 0, fixed: 0, failed: 0, skipped: 0 });

    const inScopeOrders = orders.filter((order) => {
      const createdTime = order.createdAtISO ? new Date(order.createdAtISO) : null;
      return createdTime && createdTime >= ACCESS_CUTOFF && (order.email || order.wpUserId);
    });

    setBulkSummary((prev) => ({ ...prev, total: inScopeOrders.length }));

    for (const order of inScopeOrders) {
      if (bulkCancelRef.current) break;

      const baseRow = {
        id: order.id,
        name: order.name || "—",
        email: order.email || "—",
        course: order.productName || "—",
      };

      // Step 1: check current access
      setBulkLog((prev) => [...prev, { ...baseRow, state: "checking", detail: "Checking access…" }]);
      const status = await checkAccess(order);

      if (!status.checked) {
        setBulkSummary((prev) => ({ ...prev, checked: prev.checked + 1, skipped: prev.skipped + 1 }));
        setBulkLog((prev) => prev.map((row) =>
          row.id === order.id ? { ...row, state: "skipped", detail: "Could not reach access check" } : row
        ));
        continue;
      }

      if (!status.found) {
        setBulkSummary((prev) => ({ ...prev, checked: prev.checked + 1, skipped: prev.skipped + 1 }));
        setBulkLog((prev) => prev.map((row) =>
          row.id === order.id ? { ...row, state: "skipped", detail: "No matching WordPress user" } : row
        ));
        continue;
      }

      const isOk = status.hasFullAccess || status.noCoursesConfigured;

      if (isOk) {
        setBulkSummary((prev) => ({ ...prev, checked: prev.checked + 1, alreadyOk: prev.alreadyOk + 1 }));
        setBulkLog((prev) => prev.map((row) =>
          row.id === order.id ? { ...row, state: "ok", detail: "Already has access" } : row
        ));
        setAccessStatus((prev) => ({ ...prev, [order.id]: status }));
        continue;
      }

      // Step 2: needs fixing
      setBulkLog((prev) => prev.map((row) =>
        row.id === order.id ? { ...row, state: "fixing", detail: "Fixing access…" } : row
      ));

      try {
        const result = await fixOneOrder(order);

        if (result?.success) {
          setBulkSummary((prev) => ({ ...prev, checked: prev.checked + 1, fixed: prev.fixed + 1 }));
          setBulkLog((prev) => prev.map((row) =>
            row.id === order.id ? { ...row, state: "fixed", detail: "Access granted" } : row
          ));

          // Re-check shortly after so the main table reflects the new state too
          setTimeout(async () => {
            const recheck = await checkAccess(order);
            setAccessStatus((prev) => ({ ...prev, [order.id]: recheck }));
          }, 2000);
        } else {
          setBulkSummary((prev) => ({ ...prev, checked: prev.checked + 1, failed: prev.failed + 1 }));
          setBulkLog((prev) => prev.map((row) =>
            row.id === order.id ? { ...row, state: "failed", detail: result?.error || "Unknown error" } : row
          ));
        }
      } catch (err) {
        setBulkSummary((prev) => ({ ...prev, checked: prev.checked + 1, failed: prev.failed + 1 }));
        setBulkLog((prev) => prev.map((row) =>
          row.id === order.id ? { ...row, state: "failed", detail: err?.message || "Unknown error" } : row
        ));
      }

      // Small delay between orders so we don't hammer WordPress with
      // back-to-back order creation calls.
      await new Promise((resolve) => setTimeout(resolve, 600));
    }

    setBulkRunning(false);
    setBulkDone(true);
  };

  // ─── Bulk TritekConnect invites (current filter/date range) ────────────────
  // Sends one invite per unique email in the currently filtered rows, skipping
  // healthcare-analysis orders. Sequential with a small gap, same as the
  // access repair, so we don't hammer the invite endpoint.
  const sendInvites = async () => {
    const recipients = inviteRecipients(filteredOrders);
    const range = dateFrom || dateTo ? `${dateFrom || "start"} → ${dateTo || "today"}` : "all dates";

    if (!recipients.length) return alert("No one to invite in this selection.");
    if (!window.confirm(
      `Send TritekConnect invites to ${recipients.length} people (${range})?\n` +
      `Healthcare Analysis orders are excluded.`
    )) return;

    setInviteProgress({ done: 0, total: recipients.length, failed: 0 });

    let failed = 0;
    for (const [i, order] of recipients.entries()) {
      const result = await sendTritekConnectInvite(order.email.trim());
      if (!result?.ok) failed += 1;
      setInviteProgress({ done: i + 1, total: recipients.length, failed });
      await new Promise((resolve) => setTimeout(resolve, 300));
    }

    setInviteProgress(null);
    alert(`Invites finished: ${recipients.length - failed} sent, ${failed} failed (see console).`);
  };

  const openBulkModal = () => {
    setBulkOpen(true);
    runBulkRepair();
  };

  const closeBulkModal = () => {
    if (bulkRunning) {
      bulkCancelRef.current = true;
    }
    setBulkOpen(false);
  };

  const bulkStateChip = (state) => {
    switch (state) {
      case "checking": return <Chip size="small" label="Checking…" />;
      case "fixing":    return <Chip size="small" color="warning" label="Fixing…" />;
      case "ok":        return <Chip size="small" color="success" icon={<CheckCircle />} label="Already OK" />;
      case "fixed":     return <Chip size="small" color="success" icon={<CheckCircle />} label="Fixed" />;
      case "failed":    return <Chip size="small" color="error" icon={<ErrorOutline />} label="Failed" />;
      case "skipped":   return <Chip size="small" label="Skipped" />;
      default:          return <Chip size="small" label={state} />;
    }
  };


  const handleExport = () => {
    const rows = filteredOrders.map((order) => ({
      date:          order.createdAtISO ? new Date(order.createdAtISO).toLocaleString() : "",
      name:          order.name          ?? "",
      email:         order.email         ?? "",
      phone:         order.phone         ?? "",
      staff:         getStaffName(order.staff),
      course:        order.productName   ?? "",
      paymentOption: order.paymentOption ?? "",
      amountPaid:    order.amountPaid    ?? "",
    }));
    const label = dateFrom || dateTo
      ? `${dateFrom || "start"}_to_${dateTo || "today"}`
      : "all";
    downloadCSV(rows, `orders_${label}.csv`);
  };

  // ─── Modal helpers ─────────────────────────────────────────────────────────
  const handleOpen = (order = null) => {
    setCurrentOrder(order);
    setNewOrder(order || {
      name: "", email: "", phone: "", staff: "",
      paymentPlan: "part", paymentOption: "card", amountPaid: "",
    });
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewOrder((prev) => ({ ...prev, [name]: value }));
  };

  const handleProductChange = (e) => {
    const productName = e.target.value;
    const course = courseOptions[productName];
    setNewOrder((prev) => ({
      ...prev, productName,
      productId: course?.productId || "",
      amountPaid: course ? course.price : prev.amountPaid,
    }));
  };

  // ─── Save order ────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (currentOrder) {
        await updateDoc(doc(db, "orders", currentOrder.id), {
          ...newOrder, amountPaid: Number(newOrder.amountPaid || 0), updatedAt: serverTimestamp(),
        });
      } else {
        const [firstName, ...rest] = (newOrder.name || "").trim().split(" ");
        const lastName = rest.join(" ") || "";
        const docRef = await addDoc(collection(db, "orders"), {
          ...newOrder, amountPaid: Number(newOrder.amountPaid || 0),
          createdAt: serverTimestamp(), createdAtISO: new Date().toISOString(),
          updatedAt: serverTimestamp(), status: "pending",
        });
        const paymentData = {
          amountPaid: Math.round(Number(newOrder.amountPaid || 0) * 100),
          productId: newOrder.productId || null,
          source: newOrder.paymentOption || "manual",
        };
        const result = await createUserAccount(
          { email: newOrder.email, firstName, lastName, phone: newOrder.phone },
          paymentData
        );
        await updateDoc(doc(db, "orders", docRef.id), result?.success
          ? { status: "completed", wpUserId: result?.data?.user?.id ?? null, wooOrderId: result?.data?.order?.id ?? null, updatedAt: serverTimestamp() }
          : { status: "failed", failureReason: result?.error || "Account/order creation failed", updatedAt: serverTimestamp() }
        );
      }
      handleClose();
    } catch (error) {
      console.error("Error saving order:", error);
    } finally {
      setLoading(false);
    }
  };

  // ─── Delete order ──────────────────────────────────────────────────────────
  const handleDelete = async (orderId) => {
    if (window.confirm("Are you sure you want to delete this order?")) {
      try { await deleteDoc(doc(db, "orders", orderId)); }
      catch (error) { console.error("Error deleting order:", error); }
    }
  };

  const handleSearch = (e) => setSearchQuery(e.target.value);
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => { setRowsPerPage(+event.target.value); setPage(0); };

  // ─── Access status cell renderer ──────────────────────────────────────────
  const renderAccessCell = (order) => {
    const status = accessStatus[order.id];

    if (!status) {
      return <CircularProgress size={16} />;
    }
    if (status.outOfScope) {
      return <Typography variant="caption" color="text.secondary">—</Typography>;
    }
    if (!status.checked) {
      return <Tooltip title="Could not reach access check"><Chip size="small" label="Unknown" /></Tooltip>;
    }
    if (!status.found) {
      return <Chip size="small" color="default" label="No WP user" />;
    }
    if (!status.hasAnyLevel) {
      return <Chip size="small" color="error" icon={<ErrorOutline />} label="No membership" />;
    }
    if (status.noCoursesConfigured) {
      return <Chip size="small" color="success" icon={<CheckCircle />} label="Has level" />;
    }
    if (status.hasFullAccess) {
      return <Chip size="small" color="success" icon={<CheckCircle />} label="Access OK" />;
    }
    return (
      <Tooltip title={`Missing: ${status.expectedCourses
        .filter((c) => status.missingCourses.includes(c.course_id))
        .map((c) => c.title)
        .join(", ")}`}
      >
        <Chip size="small" color="warning" icon={<ErrorOutline />} label={`Missing ${status.missingCourses.length}`} />
      </Tooltip>
    );
  };

  const needsFix = (order) => {
    const status = accessStatus[order.id];
    return status && status.checked && status.found
      && (!status.hasAnyLevel || (status.hasAnyLevel && !status.noCoursesConfigured && !status.hasFullAccess));
  };

  // ─── Block render until Firebase Auth resolves ────────────────────────────
  if (currentUser === undefined) {
    return (
      <Typography align="center" sx={{ mt: 4 }}>
        Loading...
      </Typography>
    );
  }

  const visibleRows = filteredOrders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      <Typography variant="h3" align="center">Payment Log</Typography>

      <Grid container spacing={2} sx={{ mb: 2, mt: 2 }} alignItems="center">

        {/* Add Order + Export — hidden for restricted users */}
        {!isRestricted && (
          <Grid item xs={12} md={6} sx={{ display: "flex", gap: 1 }}>
            <Button variant="contained" color="primary" onClick={() => handleOpen()}>
              Add Order
            </Button>
            <Button
              variant="outlined"
              color="success"
              startIcon={<Download />}
              onClick={handleExport}
            >
              Export CSV {(dateFrom || dateTo) ? `(${dateFrom || "…"} → ${dateTo || "…"})` : "(All)"}
            </Button>
            <Button
              variant="outlined"
              startIcon={accessChecking ? <CircularProgress size={16} /> : <Refresh />}
              onClick={() => runAccessCheck(visibleRows)}
              disabled={accessChecking}
            >
              Recheck Access
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={openBulkModal}
            >
              Fix All Access
            </Button>
            <Button
              variant="outlined"
              color="info"
              onClick={sendInvites}
              disabled={Boolean(inviteProgress)}
              startIcon={inviteProgress ? <CircularProgress size={16} /> : null}
            >
              {inviteProgress
                ? `Inviting ${inviteProgress.done}/${inviteProgress.total}…`
                : `Send TritekConnect (${dateFrom || "start"} → ${dateTo || "today"})`}
            </Button>
          </Grid>
        )}

        {/* Search */}
        <Grid item xs={12} md={isRestricted ? 12 : 6}>
          <TextField
            fullWidth
            placeholder="Search Orders"
            value={searchQuery}
            onChange={handleSearch}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
            }}
          />
        </Grid>

        {/* Date From */}
        <Grid item xs={6} md={3}>
          <TextField fullWidth label="From" type="date" value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        </Grid>

        {/* Date To */}
        <Grid item xs={6} md={3}>
          <TextField fullWidth label="To" type="date" value={dateTo}
            onChange={(e) => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        </Grid>

        {/* Payment Option Filter */}
        <Grid item xs={6} md={3}>
          <FormControl fullWidth>
            <InputLabel>Payment Option</InputLabel>
            <Select name="paymentOption" value={filters.paymentOption} label="Payment Option" onChange={handleFilterChange}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="klarna">Klarna</MenuItem>
              <MenuItem value="clearpay">ClearPay</MenuItem>
              <MenuItem value="super-paylater">Super Pay Later</MenuItem>
              <MenuItem value="super">Super (Card / Bank)</MenuItem>
              <MenuItem value="flexpay-uk">Card Payment UK</MenuItem>
              <MenuItem value="flexpay-us">Card Payment US</MenuItem>
              <MenuItem value="flexpay-can">Card Payment CAD</MenuItem>
              <MenuItem value="transfer">Transfer</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Course Filter */}
        <Grid item xs={6} md={3}>
          <FormControl fullWidth>
            <InputLabel>Course</InputLabel>
            <Select name="course" label="Course" value={filters.course} onChange={handleFilterChange}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="PM">Project Management</MenuItem>
              <MenuItem value="BA">Business Analysis</MenuItem>
              <MenuItem value="PMBA">PMBA</MenuItem>
              <MenuItem value="PMDA">PMDA</MenuItem>
              <MenuItem value="BADA">BADA</MenuItem>
              <MenuItem value="Data Analysis">Data Analysis</MenuItem>
              <MenuItem value="Cybersecurity">Cybersecurity</MenuItem>
              <MenuItem value="Digital Marketing">Digital Marketing</MenuItem>
              <MenuItem value="Product Management">Product Management</MenuItem>
              <MenuItem value="Health Analyitcs">Health Analytics</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Orders Table */}
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableHead sx={{ backgroundColor: "#1976d2" }}>
            <TableRow>
              <TableCell sx={{ color: "#fff" }}>Date</TableCell>
              <TableCell sx={{ color: "#fff" }}>Name</TableCell>
              <TableCell sx={{ color: "#fff" }}>Email</TableCell>
              <TableCell sx={{ color: "#fff" }}>Phone Number</TableCell>
              <TableCell sx={{ color: "#fff" }}>Staff</TableCell>
              <TableCell sx={{ color: "#fff" }}>Course</TableCell>
              <TableCell sx={{ color: "#fff" }}>Payment Option</TableCell>
              <TableCell sx={{ color: "#fff" }}>Amount Paid</TableCell>
              <TableCell sx={{ color: "#fff" }}>Access Status</TableCell>
              {!isRestricted && <TableCell sx={{ color: "#fff" }}>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((order, index) => (
                <TableRow key={order.id} sx={{ backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#fff" }}>
                  <TableCell>
                    {order.createdAtISO ? new Date(order.createdAtISO).toLocaleString() : "-"}
                  </TableCell>
                  <TableCell>{order.name}</TableCell>
                  <TableCell>{order.email}</TableCell>
                  <TableCell>{order.phone}</TableCell>
                  <TableCell>{getStaffName(order.staff)}</TableCell>
                  <TableCell>{order.productName}</TableCell>
                  <TableCell>{order.paymentOption}</TableCell>
                  <TableCell>{order.amountPaid}</TableCell>
                  <TableCell>{renderAccessCell(order)}</TableCell>
                  {!isRestricted && (
                    <TableCell>
                      <IconButton onClick={() => handleOpen(order)}><Edit /></IconButton>
                      <IconButton onClick={() => handleDelete(order.id)}><Delete /></IconButton>
                      {needsFix(order) && (
                        <Button
                          size="small"
                          variant="contained"
                          color="warning"
                          disabled={fixingOrderId === order.id}
                          onClick={() => handleFixAccess(order)}
                          sx={{ ml: 1 }}
                        >
                          {fixingOrderId === order.id ? "Fixing…" : "Fix Access"}
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredOrders.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Add/Edit Order Modal — only accessible to non-restricted users */}
      {!isRestricted && (
        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>{currentOrder ? "Edit Order" : "Add New Order"}</DialogTitle>
          <DialogContent>
            <TextField margin="dense" label="Name" type="text" name="name" fullWidth value={newOrder.name} onChange={handleChange} />
            <TextField margin="dense" label="Email" type="email" name="email" fullWidth value={newOrder.email} onChange={handleChange} />
            <TextField margin="dense" label="Phone Number" type="text" name="phone" fullWidth value={newOrder.phone} onChange={handleChange} />
            <Autocomplete
              options={staffList} value={newOrder.staff}
              onChange={(e, value) => setNewOrder((prev) => ({ ...prev, staff: value }))}
              renderInput={(params) => <TextField {...params} label="Staff" />}
              fullWidth margin="dense"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Product</InputLabel>
              <Select name="productName" label="Product" value={newOrder.productName || ""} onChange={handleProductChange}>
                {courseList.map(([name, meta]) => (
                  <MenuItem key={meta.productId + name} value={name}>{name} — £{meta.price}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl component="fieldset" margin="dense">
              <RadioGroup row name="paymentOption" value={newOrder.paymentOption} onChange={handleChange}>
                <FormControlLabel value="klarna"      control={<Radio />} label="Klarna" />
                <FormControlLabel value="clearpay"    control={<Radio />} label="ClearPay" />
                <FormControlLabel value="flexpay-uk"  control={<Radio />} label="Card Payment UK" />
                <FormControlLabel value="flexpay-us"  control={<Radio />} label="Card Payment US" />
                <FormControlLabel value="flexpay-can" control={<Radio />} label="Card Payment CAD" />
                <FormControlLabel value="transfer"    control={<Radio />} label="Transfer" />
              </RadioGroup>
            </FormControl>
            <TextField margin="dense" label="Amount Paid" type="number" name="amountPaid" fullWidth value={newOrder.amountPaid} onChange={handleChange} />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="secondary">Cancel</Button>
            <Button onClick={handleSubmit} color="primary" disabled={loading}>
              {loading ? "Saving..." : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Bulk Access Repair Modal */}
      <Dialog open={bulkOpen} onClose={closeBulkModal} maxWidth="md" fullWidth>
        <DialogTitle>
          Fix All Access
          {bulkRunning && <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>Running — please keep this open until finished</Typography>}
          {bulkDone && <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>Finished</Typography>}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <Chip label={`Total: ${bulkSummary.total}`} />
            <Chip label={`Checked: ${bulkSummary.checked}`} />
            <Chip color="success" label={`Already OK: ${bulkSummary.alreadyOk}`} />
            <Chip color="success" label={`Fixed: ${bulkSummary.fixed}`} />
            <Chip color="error" label={`Failed: ${bulkSummary.failed}`} />
            <Chip label={`Skipped: ${bulkSummary.skipped}`} />
          </Box>

          {bulkRunning && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">
                Processing {bulkSummary.checked} of {bulkSummary.total}…
              </Typography>
            </Box>
          )}

          <TableContainer component={Paper} sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Course</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Detail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bulkLog.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">Starting…</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {bulkLog.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.email}</TableCell>
                    <TableCell>{row.course}</TableCell>
                    <TableCell>{bulkStateChip(row.state)}</TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{row.detail}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          {bulkRunning ? (
            <Button onClick={closeBulkModal} color="secondary">Stop</Button>
          ) : (
            <>
              {bulkDone && <Button onClick={openBulkModal} color="primary">Run again</Button>}
              <Button onClick={closeBulkModal} color="secondary">Close</Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default OrdersTable;